/**
 * MIDI Matrix — the rig-wide map of which CC/note on which channel is
 * claimed by which device(s) + controls.
 *
 * Replaces ortopilot's external Google Sheet ("midi matrix og.html"). One
 * source of truth: adding a control in OPButtons browses the matrix for free
 * slots; the matrix view shows the whole rig at a glance.
 *
 * Phase 1 (current): read-only viewer + picker integration. Bridge persists
 * the file; PWA renders and reads from it. Editing the matrix inside OPButtons
 * lands in Phase 2.
 *
 * Note vs CC representation:
 *   - CCs are numbers 0..127. Channel is 1..16.
 *   - Notes are stored as LETTER LABELS ("C3", "F#4") because some
 *     manufacturers offset by ±12 semitones vs canonical MIDI note number.
 *     For ortopilot the letter is the source of truth — same letter on the
 *     same channel = clash, regardless of which manufacturer's MIDI number
 *     they translate to. The optional `noteMidi` is bookkeeping, not the
 *     identity.
 *
 * Shape — slot vs claim:
 *   A MIDI signal (kind + channel + ccNumber|noteLabel) is one Slot. One
 *   or more SlotClaims live on that slot — each claim is one device + one
 *   control. Multiple claims on one slot = "co-owners," intentional in
 *   ortopilot's rig (e.g. PCR#1-1 and TOSC2 both transmit CC 1 ch 13 to
 *   trigger Scene 1; the slot has two claims and the viewer renders the
 *   primary claim's tint with a "2×" chip showing the share count).
 */

// =====================================================================
// Devices
// =====================================================================

/** Editor-only color group. Reuses the existing PageHue palette so the
 *  matrix shares the project's semantic color system. The schema stores
 *  the token string; the PWA resolves it via the --page-hue-* CSS vars. */
export type MatrixDeviceColor =
  | 'sky' | 'orchid' | 'amber' | 'lime'
  | 'rose' | 'forest' | 'cyan' | 'magenta';

export interface MatrixDevice {
  /** Stable id. ULID. */
  id: string;
  /** Display name, e.g. "APC40 mk2 #1", "Midi Fighter Twister",
   *  "Stream Deck XL". User-renamable. */
  name: string;
  /** Editor-only color group — drives the cell tint in the matrix view
   *  so devices visually cluster. */
  color: MatrixDeviceColor;
  /** Optional free text — model number, firmware version, location in
   *  rack, anything else that helps ortopilot remember context. */
  notes?: string;
}

// =====================================================================
// Slots + Claims
// =====================================================================

/** A slot is a CC, a note, or a Program Change claim. The kind discriminator
 *  selects which fields are meaningful.
 *
 *  `pc` joined 2026-08-10. Program Changes were outside the rig map until the
 *  inspector's Send browser started offering a PC list and had nothing to
 *  show it - the operator's call was to teach the Matrix rather than leave a
 *  third of the browser permanently empty. A program number is its own
 *  namespace: PC 5 on channel 1 collides only with another PC 5 on channel 1,
 *  never with CC 5 or note 5. */
export type MatrixSlotKind = 'cc' | 'note' | 'pc';

/** One device + one control claiming a slot. Multiple claims on the same
 *  slot represent intentional co-ownership (e.g. PCR + TouchOSC both
 *  send the same MIDI message to trigger the same musical action). */
export interface MatrixClaim {
  /** Stable id. ULID. */
  id: string;
  /** Which device this claim belongs to. References MatrixDevice.id. */
  deviceId: string;
  /** Human label for the control on the device, e.g. "Fader 1",
   *  "Pad 4", "Scene 1". */
  controlLabel: string;
  /** Optional free text — purpose ("kick filter cutoff"), gig assignment,
   *  anything. Editor only. */
  notes?: string;
  /** Set when an OPButtons control OWNS this claim: it was made by picking
   *  this square from the control's Inspector (2026-10-18, OPBridge S317).
   *  The claim follows that control - a new binding moves it, a rename
   *  relabels it, unbinding or deleting removes it (`matrix/itemClaims.ts`).
   *  Absent = a claim made by hand in the matrix viewer, never touched by an
   *  item edit. */
  itemId?: string;
}

export interface MatrixSlot {
  /** Stable id. ULID. */
  id: string;
  /** Either 'cc' (use ccNumber) or 'note' (use noteLabel + optional noteMidi). */
  kind: MatrixSlotKind;
  /** MIDI channel 1..16. */
  channel: number;
  /** CC controller number 0..127. Required when kind = 'cc', ignored otherwise. */
  ccNumber?: number;
  /** Letter form, e.g. "C3", "F#4", "Bb-1". Source of truth for note
   *  identity. Required when kind = 'note', ignored otherwise. */
  noteLabel?: string;
  /** Canonical MIDI note number 0..127 when known. Bookkeeping only —
   *  clash detection uses noteLabel, not this. */
  noteMidi?: number;
  /** Program number 0..127. Required when kind = 'pc', ignored otherwise.
   *  A plain number rather than a label: unlike notes, nobody transposes a
   *  program number, so there is no manufacturer-offset caveat to model. */
  pcNumber?: number;
  /** One or more device claims on this signal. First claim is the
   *  PRIMARY — its hue tints the cell, its label is the visible text.
   *  Additional claims render as a "2×"/"3×" chip and surface in tooltip. */
  claims: MatrixClaim[];
}

// =====================================================================
// Library document
// =====================================================================

export const MATRIX_SCHEMA_VERSION = 'opbuttons-matrix/v2' as const;

export interface MatrixLibrary {
  schemaVersion: typeof MATRIX_SCHEMA_VERSION;
  /** All known devices, in display order. */
  devices: MatrixDevice[];
  /** Every claimed slot. The viewer pivots these into a (channel × number)
   *  grid; the storage layer is a flat list so adding/removing is cheap
   *  and ordering is irrelevant. */
  slots: MatrixSlot[];
}

/** Empty seed used when no matrix.json exists yet AND no public seed
 *  ships with this build. The store first tries `/matrix-seed.json` and
 *  falls back to this when that fetch fails or returns nothing. */
export function emptyMatrixLibrary(): MatrixLibrary {
  return {
    schemaVersion: MATRIX_SCHEMA_VERSION,
    devices: [],
    slots: [],
  };
}

// =====================================================================
// Helpers
// =====================================================================

/** A normalized identity for clash comparison. Two slots clash iff their
 *  slotKeys are equal. For CC: `cc:<channel>:<number>`. For notes:
 *  `note:<channel>:<label-uppercased>` (letter is the identity per the
 *  ±12 manufacturer-offset caveat). For program changes:
 *  `pc:<channel>:<number>`.
 *
 *  The kind is part of the key, which is what keeps the three namespaces
 *  apart: CC 5, note 5 and PC 5 on one channel are three different signals
 *  and must never be reported as a clash. */
export function slotKey(
  slot: Pick<MatrixSlot, 'kind' | 'channel' | 'ccNumber' | 'noteLabel' | 'pcNumber'>,
): string {
  if (slot.kind === 'cc') {
    return `cc:${slot.channel}:${slot.ccNumber ?? '?'}`;
  }
  if (slot.kind === 'pc') {
    return `pc:${slot.channel}:${slot.pcNumber ?? '?'}`;
  }
  return `note:${slot.channel}:${(slot.noteLabel ?? '?').toUpperCase()}`;
}

/** Look up the slot occupying a given (kind, channel, label/number)
 *  triple. Returns undefined when free. */
export function findSlot(
  library: MatrixLibrary,
  query:
    | { kind: 'cc'; channel: number; ccNumber: number }
    | { kind: 'note'; channel: number; noteLabel: string }
    | { kind: 'pc'; channel: number; pcNumber: number }
): MatrixSlot | undefined {
  const key = query.kind === 'cc'
    ? slotKey({ kind: 'cc', channel: query.channel, ccNumber: query.ccNumber })
    : query.kind === 'pc'
      ? slotKey({ kind: 'pc', channel: query.channel, pcNumber: query.pcNumber })
      : slotKey({ kind: 'note', channel: query.channel, noteLabel: query.noteLabel });
  return library.slots.find((s) => slotKey(s) === key);
}

/** Lookup helper — get the device record for a claim. Returns undefined
 *  if the claim points to a device that no longer exists. */
export function deviceForClaim(
  library: MatrixLibrary,
  claim: MatrixClaim
): MatrixDevice | undefined {
  return library.devices.find((d) => d.id === claim.deviceId);
}

/** Primary claim — the first claim on a slot. Used as the visible label /
 *  tint. Slots with no claims (shouldn't happen normally) return undefined. */
export function primaryClaim(slot: MatrixSlot): MatrixClaim | undefined {
  return slot.claims[0];
}
